import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="account-search"
// Renders a dropdown of account search results and navigates to account on click.
export default class extends Controller {
  static values = {
    url: { type: String, default: "/accounts/search" },
    minLength: { type: Number, default: 2 },
    debounceMs: { type: Number, default: 250 },
    noResultsText: { type: String, default: "No accounts found." },
    searchingText: { type: String, default: "Searching…" }
  }

  static targets = ["input", "results"]

  connect() {
    this.abortController = null
    this.debounceTimer = null
    this.boundHideResults = this.hideResults.bind(this)
    this.boundHandleKeydown = this.handleKeydown.bind(this)
    this.boundFocusSearch = this.focusSearch.bind(this)
    document.addEventListener("keydown", this.boundFocusSearch)
  }

  disconnect() {
    this.cancelFetch()
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    document.removeEventListener("keydown", this.boundFocusSearch)
    document.removeEventListener("click", this.boundHideResults)
    document.removeEventListener("keydown", this.boundHandleKeydown)
  }

  // Cmd+K (Mac) / Ctrl+K (Windows, Linux) — focus search like GitHub, Slack, Notion
  focusSearch(e) {
    if ((e.metaKey || e.ctrlKey) && e.key === "k") {
      e.preventDefault()
      const active = document.activeElement
      const inField = active?.closest?.("input, textarea, [contenteditable=true]")
      if (!inField || this.element.contains(active)) {
        this.inputTarget.focus()
      }
    }
  }

  input() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.performSearch(), this.debounceMsValue)
  }

  performSearch() {
    const q = this.inputTarget.value?.trim() || ""
    if (q.length < this.minLengthValue) {
      this.resultsTarget.hidden = true
      this.resultsTarget.innerHTML = ""
      return
    }

    this.cancelFetch()
    this.abortController = new AbortController()
    this.showLoading()

    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("q", q)

    fetch(url, {
      signal: this.abortController.signal,
      headers: { "Accept": "application/json", "X-Requested-With": "XMLHttpRequest" }
    })
      .then(res => res.ok ? res.json() : [])
      .then(data => this.renderResults(data))
      .catch(() => { this.resultsTarget.hidden = true })
      .finally(() => { this.abortController = null })
  }

  showLoading() {
    const list = this.resultsTarget
    list.innerHTML = `
      <li class="flex items-center gap-2 p-3 text-base-content/70">
        <span class="loading loading-spinner loading-sm" aria-hidden="true"></span>
        <span>${this.escapeHtml(this.searchingTextValue)}</span>
      </li>
    `
    list.hidden = false
  }

  renderResults(items) {
    const list = this.resultsTarget
    if (!items.length) {
      list.innerHTML = `
        <li class="p-3 text-base-content/60" role="status">
          ${this.escapeHtml(this.noResultsTextValue)}
        </li>
      `
      list.hidden = false
    } else {
      list.innerHTML = items.map(a => `
        <li>
          <a href="${this.escapeHtml(a.url)}" data-turbo-frame="_top">
            <span class="font-medium">${this.escapeHtml(a.full_name)}</span>
            <span class="text-base-content/60 ml-2">OIB: ${this.escapeHtml(a.oib)}</span>
            <span class="block text-base-content/60 text-xs mt-0.5">${this.escapeHtml(a.email)}</span>
          </a>
        </li>
      `).join("")
      list.hidden = false
    }

    document.addEventListener("click", this.boundHideResults, { once: true, capture: true })
    this.inputTarget.addEventListener("keydown", this.boundHandleKeydown, { once: true })
  }

  hideResults(e) {
    if (!this.hasResultsTarget) return
    if (e?.type === "keydown") {
      if (e.key === "Escape") {
        this.resultsTarget.hidden = true
        this.inputTarget.blur()
      }
      return
    }
    if (!this.element.contains(e?.target)) {
      this.resultsTarget.hidden = true
    }
  }

  handleKeydown(e) {
    if (e.key === "Escape") {
      this.resultsTarget.hidden = true
      this.inputTarget.blur()
    }
  }

  cancelFetch() {
    if (this.abortController) {
      this.abortController.abort()
    }
  }

  escapeHtml(str) {
    if (str == null) return ""
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }
}
